import React from "react";
import { motion } from "framer-motion";
import { Mail, Phone } from "lucide-react";
import { useLang } from "@/context/LangContext";
import { BRAND, waLink } from "@/data/brand";
import ContactForm from "@/components/ContactForm";
import { WhatsAppIcon } from "@/components/WhatsAppButton";

export default function ContactFooter() {
  const { t } = useLang();

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="relative bg-[#0B1712] pt-24 sm:pt-32 border-t border-white/10 overflow-hidden"
    >
      <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="font-mono text-xs tracking-[0.25em] uppercase text-[color:var(--dp-orange)] mb-4"
        >
          {t.contact.overline}
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="font-heading font-black uppercase tracking-tighter text-white leading-[0.85] text-[18vw] sm:text-[13vw] lg:text-[11vw]"
        >
          {t.contact.big}
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mt-12 pb-24">
          <div className="lg:col-span-5">
            <p className="text-white/60 font-light text-lg max-w-md">{t.contact.sub}</p>

            <div className="mt-10 space-y-5">
              <a
                href={`mailto:${BRAND.email}`}
                data-testid="contact-email-link"
                className="group flex items-center gap-4 border border-white/10 hover:border-[color:var(--dp-orange)] p-5 transition-colors"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[color:var(--dp-orange)] text-black shrink-0">
                  <Mail size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50">
                    {t.contact.email}
                  </span>
                  <span className="text-white text-lg font-medium group-hover:text-[color:var(--dp-orange)] transition-colors">
                    {BRAND.email}
                  </span>
                </span>
              </a>

              <a
                href={`tel:+${BRAND.whatsapp}`}
                data-testid="contact-phone-block"
                className="group flex items-center gap-4 border border-white/10 hover:border-[color:var(--dp-orange)] p-5 transition-colors"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[color:var(--dp-orange)] text-black shrink-0">
                  <Phone size={20} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50">
                    {t.contact.phone}
                  </span>
                  <span className="text-white text-lg font-medium group-hover:text-[color:var(--dp-orange)] transition-colors">
                    {BRAND.phoneDisplay}
                  </span>
                </span>
              </a>

              <a
                href={waLink(t.contact.wa.prefill)}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-whatsapp-link"
                className="group flex items-center gap-4 border border-white/10 hover:border-[#25D366] p-5 transition-colors"
              >
                <span className="flex h-11 w-11 items-center justify-center bg-[#25D366] text-white shrink-0">
                  <WhatsAppIcon size={22} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-widest text-white/50">
                    {t.contact.wa.label}
                  </span>
                  <span className="text-white text-lg font-medium group-hover:text-[#25D366] transition-colors">
                    {BRAND.phoneDisplay}
                  </span>
                </span>
              </a>
            </div>
          </div>

          <div className="lg:col-span-7 lg:pl-8">
            <div className="border border-white/10 bg-[#112318] p-6 sm:p-8">
              <ContactForm />
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 py-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="flex items-center gap-4">
            <img
              src={BRAND.logoFull}
              alt="DPNox Emissions Solutions"
              className="h-16 w-auto object-contain"
            />
            <span className="text-white/40 text-sm font-light border-l border-white/15 pl-4">
              {t.footer.tagline}
            </span>
          </div>
          <p className="font-mono text-[11px] tracking-widest uppercase text-white/40">
            © {new Date().getFullYear()} DPNox · {t.footer.rights}
          </p>
        </div>
      </div>
    </section>
  );
}
